import { useEffect, useRef, useState } from "react";
import { Loader2, Pencil } from "lucide-react";

export function EditableConversationTitle({
  title,
  onSave,
  showEditButton = false,
  editOnTitleClick = true,
}: {
  title: string;
  onSave: (title: string) => Promise<void>;
  showEditButton?: boolean;
  editOnTitleClick?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(title);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setValue(title);
  }, [editing, title]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const startEditing = () => {
    setValue(title);
    setError(false);
    setEditing(true);
  };

  const save = async () => {
    if (savingRef.current) return;
    const nextTitle = value.trim();
    if (!nextTitle) {
      setError(true);
      inputRef.current?.focus();
      return;
    }
    if (nextTitle === title) {
      setEditing(false);
      return;
    }

    savingRef.current = true;
    setSaving(true);
    setError(false);
    try {
      await onSave(nextTitle);
      setEditing(false);
    } catch {
      setError(true);
      inputRef.current?.focus();
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <form
        className="flex w-full min-w-0 flex-1 items-center gap-1"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <input
          ref={inputRef}
          value={value}
          maxLength={255}
          aria-label="Conversation title"
          aria-invalid={error}
          onChange={(event) => {
            setValue(event.target.value);
            setError(false);
          }}
          onBlur={() => void save()}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              setEditing(false);
            }
          }}
          className={`h-7 min-w-0 flex-1 rounded border bg-neutral-900 px-2 text-sm font-semibold text-neutral-100 outline-none ${
            error
              ? "border-red-500/70 focus:border-red-400"
              : "border-sky-500/60 focus:border-sky-400"
          }`}
        />
        {saving && (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-neutral-500" />
        )}
      </form>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-1 items-center gap-1">
      {editOnTitleClick ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            startEditing();
          }}
          className="block min-w-0 flex-1 truncate text-left text-sm font-semibold text-neutral-200 hover:text-neutral-100"
          title="Edit conversation title"
        >
          {title}
        </button>
      ) : (
        <span
          className="block min-w-0 flex-1 truncate text-xs font-semibold text-neutral-200 group-hover:text-neutral-100"
          title={title}
        >
          {title}
        </span>
      )}
      {showEditButton && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            startEditing();
          }}
          className="shrink-0 rounded-md p-1 text-neutral-600 opacity-0 transition-all group-hover:opacity-100 hover:bg-neutral-700/60 hover:text-neutral-200 focus:opacity-100"
          title="Edit conversation title"
          aria-label="Edit conversation title"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
